/**
 * RETOLOGIX BLE PROTOCOL SPECIFICATION (V1.0)
 * Giao thức truyền thông BLE nhị phân giữa RETOBLOCK Studio và Robot RETOCAR-STARTER (ESP32)
 */

export const BLE_CONFIG = {
  SERVICE_UUID: '4e4c5800-1000-4000-8000-00805f9b34fb',
  RX_UUID: '4e4c5801-1000-4000-8000-00805f9b34fb',
  TX_UUID: '4e4c5802-1000-4000-8000-00805f9b34fb',
  HEADER_1: 0xAA,
  HEADER_2: 0x55,
  TAIL_BYTE: 0xED,
  MAX_CHUNK_SIZE: 120
};

export const COMMANDS = {
  HEARTBEAT: 0x00,
  PING_IDENTIFY: 0x06,
  TELEMETRY: 0x10,
  FILE_START: 0x20,
  FILE_DATA: 0x21,
  FILE_END: 0x22,
  OTA_TRIGGER: 0x30,
  OTA_BEGIN: 0x30,
  OTA_READY: 0x31,
  OTA_DATA: 0x32,
  OTA_END: 0x33,
  OTA_RESULT: 0x34,
  OTA_ABORT: 0x35,
  OTA_ACK: 0x36,
  CALIBRATE_COLOR: 0x40,
  GET_LOGS: 0x50,
  LOG_DATA: 0x51,
  RUNTIME_ERROR: 0xE0,
  EMERGENCY_STOP: 0xFF
};

export const ERROR_TYPES = {
  0x01: 'Lỗi cú pháp / Thụt lề (Syntax/Indentation Error)',
  0x02: 'Lỗi tính toán / Thực thi (Math/Runtime Error)',
  0x03: 'Mất ngoại vi / Lỗi Bus I2C (Peripheral/I2C Loss)',
  0x04: 'Lỗi nguồn điện / Sụt áp (Hardware/Power Fault)'
};

export const COLOR_NAMES = {
  0: 'UNKNOWN',
  1: 'RED',
  2: 'GREEN',
  3: 'BLUE',
  4: 'YELLOW',
  5: 'WHITE',
  6: 'BLACK',
  7: 'BROWN',
  8: 'PURPLE',
  255: 'DISCONNECTED'
};

export const COLOR_DISPLAY_NAMES = {
  0: 'Không xác định (UNKNOWN)',
  1: 'ĐỎ (RED)',
  2: 'XANH LÁ (GREEN)',
  3: 'XANH DƯƠNG (BLUE)',
  4: 'VÀNG (YELLOW)',
  5: 'TRẮNG (WHITE)',
  6: 'ĐEN (BLACK)',
  7: 'NÂU (BROWN)',
  8: 'TÍM (PURPLE)',
  255: '⚠️ Mất kết nối I2C'
};

/**
 * Bảng tra cứu trước CRC-32 chuẩn IEEE 802.3
 */
const CRC32_TABLE = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let j = 0; j < 8; j++) {
    c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
  }
  CRC32_TABLE[i] = c >>> 0;
}

/**
 * Tính mã CRC-32 (IEEE 802.3 Ethernet / ZIP / ESP-IDF) cho file nhị phân OTA
 */
export function calculateCRC32(dataBytes) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < dataBytes.length; i++) {
    crc = CRC32_TABLE[(crc ^ dataBytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

/**
 * Tính mã kiểm tra toàn vẹn XOR:
 * CHECKSUM = CMD_ID ^ LENGTH ^ PAYLOAD[0] ^ ... ^ PAYLOAD[N-1]
 */
export function calculateChecksum(cmdId, length, payloadBytes) {
  let cs = cmdId ^ length;
  for (let i = 0; i < payloadBytes.length; i++) {
    cs ^= payloadBytes[i];
  }
  return cs & 0xFF;
}

/**
 * Tính CRC-16 (CCITT 0x1021) cho toàn bộ file Python
 */
export function calculateCRC16(dataBytes) {
  let crc = 0xFFFF;
  for (let i = 0; i < dataBytes.length; i++) {
    crc ^= (dataBytes[i] << 8);
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ 0x1021) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }
  return crc;
}

/**
 * Đóng gói khung nhị phân chuẩn 7 phần:
 * [0xAA, 0x55, CMD_ID, LENGTH, ...PAYLOAD, CHECKSUM, 0xED]
 */
export function buildFrame(cmdId, payloadArray = []) {
  const payloadBytes = payloadArray instanceof Uint8Array ? payloadArray : new Uint8Array(payloadArray);
  const length = payloadBytes.length;
  const checksum = calculateChecksum(cmdId, length, payloadBytes);
  const frame = new Uint8Array(6 + length);

  frame[0] = BLE_CONFIG.HEADER_1;
  frame[1] = BLE_CONFIG.HEADER_2;
  frame[2] = cmdId;
  frame[3] = length;
  frame.set(payloadBytes, 4);
  frame[4 + length] = checksum;
  frame[4 + length + 1] = BLE_CONFIG.TAIL_BYTE;

  return frame;
}

/**
 * Tạo gói khởi tạo truyền tệp (CMD 0x20):
 * Payload: FileSize (uint16_t BE), TotalChunks (uint16_t BE)
 */
export function buildFileStartPacket(fileSize, totalChunks) {
  const payload = new Uint8Array(4);
  payload[0] = (fileSize >> 8) & 0xFF;
  payload[1] = fileSize & 0xFF;
  payload[2] = (totalChunks >> 8) & 0xFF;
  payload[3] = totalChunks & 0xFF;
  return buildFrame(COMMANDS.FILE_START, payload);
}

/**
 * Tạo gói đoạn mã nguồn Python (CMD 0x21):
 * Payload: Index (uint16_t BE) + Chunk bytes
 */
export function buildFileDataPacket(packetIndex, chunkBytes) {
  const payload = new Uint8Array(2 + chunkBytes.length);
  payload[0] = (packetIndex >> 8) & 0xFF;
  payload[1] = packetIndex & 0xFF;
  payload.set(chunkBytes, 2);
  return buildFrame(COMMANDS.FILE_DATA, payload);
}

/**
 * Tạo gói kết thúc truyền tệp và kích hoạt chạy (CMD 0x22):
 * Payload: CRC-16 (uint16_t BE)
 */
export function buildFileEndPacket(crc16) {
  const payload = new Uint8Array(2);
  payload[0] = (crc16 >> 8) & 0xFF;
  payload[1] = crc16 & 0xFF;
  return buildFrame(COMMANDS.FILE_END, payload);
}

/**
 * Tạo gói lệnh dừng khẩn cấp STOP (CMD 0xFF)
 */
export function buildStopPacket() {
  return buildFrame(COMMANDS.EMERGENCY_STOP, []);
}

/**
 * Tạo gói nhịp tim Heartbeat Ping (CMD 0x00)
 */
export function buildHeartbeatPacket() {
  return buildFrame(COMMANDS.HEARTBEAT, []);
}

/**
 * Tạo gói Ping nhận diện xe (CMD 0x06)
 */
export function buildPingPacket() {
  return buildFrame(COMMANDS.PING_IDENTIFY, []);
}

/**
 * Tạo gói yêu cầu cân chỉnh màu (CMD 0x40) - Đã bãi bỏ (Deprecated)
 */
export function buildCalibratePacket() {
  return buildFrame(COMMANDS.CALIBRATE_COLOR, []);
}

/**
 * Tạo gói tin yêu cầu lấy log 5 phút từ RAM robot (CMD 0x50)
 */
export function buildGetLogsPacket() {
  return buildFrame(COMMANDS.GET_LOGS, []);
}



/**
 * Giải mã chuỗi UTF-8 an toàn tuyệt đối trên mọi môi trường (Browser, Node.js, React Native Hermes)
 */
export function safeDecodeUtf8(bytes) {
  if (!bytes || bytes.length === 0) return '';
  if (typeof TextDecoder !== 'undefined') {
    try {
      return new TextDecoder('utf-8').decode(bytes);
    } catch {
      // Fallback bên dưới
    }
  }
  let str = '';
  for (let i = 0; i < bytes.length; i++) {
    const code = bytes[i];
    if (code === 0) break;
    str += String.fromCharCode(code);
  }
  return str;
}

/**
 * Mã hóa chuỗi sang mảng byte UTF-8 an toàn trên React Native Hermes & Browser
 */
export function safeEncodeUtf8(str) {
  if (!str) return new Uint8Array(0);
  if (typeof TextEncoder !== 'undefined') {
    try {
      return new TextEncoder().encode(str);
    } catch {
      // Fallback bên dưới
    }
  }
  const utf8 = unescape(encodeURIComponent(str));
  const bytes = new Uint8Array(utf8.length);
  for (let i = 0; i < utf8.length; i++) {
    bytes[i] = utf8.charCodeAt(i);
  }
  return bytes;
}

/**
 * Chia nhỏ chuỗi Python UTF-8 thành mảng các chunks (<= 120 bytes)
 */
export function preparePythonChunks(pythonSource) {
  const fileBytes = safeEncodeUtf8(pythonSource);
  const fileSize = fileBytes.length;
  const maxChunk = BLE_CONFIG.MAX_CHUNK_SIZE;
  const totalChunks = Math.ceil(fileSize / maxChunk);

  const chunks = [];
  for (let i = 0; i < totalChunks; i++) {
    const start = i * maxChunk;
    const end = Math.min(start + maxChunk, fileSize);
    chunks.push(fileBytes.slice(start, end));
  }

  const crc16 = calculateCRC16(fileBytes);
  return {
    fileBytes,
    fileSize,
    totalChunks,
    chunks,
    crc16
  };
}

/**
 * Giải mã khung tin nhị phân nhận từ robot
 */
export function parseFrame(bytes) {
  if (bytes.length < 6) return null;
  if (bytes[0] !== BLE_CONFIG.HEADER_1 || bytes[1] !== BLE_CONFIG.HEADER_2) return null;

  const cmdId = bytes[2];
  let length = bytes[3];

  // Nếu khung tin lớn hơn 260 bytes (ví dụ gói CMD_OTA_DATA), chiều dài payload được suy ra từ kích thước toàn khung
  if (bytes.length > 260 && bytes[bytes.length - 1] === BLE_CONFIG.TAIL_BYTE) {
    length = bytes.length - 6;
  }

  if (bytes.length < 6 + length) return null;

  const tailIndex = 5 + length;
  if (bytes[tailIndex] !== BLE_CONFIG.TAIL_BYTE) return null;

  const payload = bytes.slice(4, 4 + length);
  const expectedCs = bytes[4 + length];
  const actualCs = calculateChecksum(cmdId, length, payload);

  if (expectedCs !== actualCs) {
    console.warn(`Checksum mismatch: expected 0x${expectedCs.toString(16)}, got 0x${actualCs.toString(16)}`);
    return null;
  }

  return {
    cmdId,
    length,
    payload
  };
}

/**
 * Giải mã gói Telemetry (CMD 0x10) - 10 bytes payload
 */
export function parseTelemetry(payload) {
  if (payload.length < 10) return null;

  const distance = (payload[0] << 8) | payload[1];
  const lineBits = payload[2];
  const s1 = Boolean(lineBits & 0b001);
  const s2 = Boolean(lineBits & 0b010);
  const s3 = Boolean(lineBits & 0b100);

  const lightPercent = payload[3];
  const colorId = payload[4];
  const colorName = COLOR_NAMES[colorId] || 'UNKNOWN';

  const r = payload[5];
  const g = payload[6];
  const b = payload[7];

  const buttonBits = payload[8];
  const bt1 = Boolean(buttonBits & 0b01);
  const bt2 = Boolean(buttonBits & 0b10);

  const robotState = payload[9]; // 0: IDLE, 1: RUNNING

  let firmwareVersion = null;
  if (payload.length > 10) {
    try {
      const verBytes = payload.slice(10);
      const rawVer = safeDecodeUtf8(verBytes).replace(/\0/g, '').trim();
      if (rawVer.length > 0) {
        firmwareVersion = rawVer.startsWith('v') ? rawVer : `v${rawVer}`;
      }
    } catch {
      firmwareVersion = null;
    }
  }

  return {
    distance,
    line: { s1, s2, s3, rawBits: lineBits },
    lightPercent,
    color: { id: colorId, name: colorName, r, g, b },
    buttons: { bt1, bt2 },
    robotState,
    isRunning: robotState === 1,
    firmwareVersion
  };
}

/**
 * Giải mã gói thông báo lỗi Runtime (CMD 0xE0)
 */
export function parseRuntimeError(payload) {
  if (payload.length < 3) return null;

  const errorCode = payload[0];
  const lineNum = (payload[1] << 8) | payload[2];
  const message = payload.length > 3 ? safeDecodeUtf8(payload.slice(3)) : 'Lỗi không xác định';
  const errorType = ERROR_TYPES[errorCode] || 'Lỗi thực thi (Runtime Error)';

  return {
    errorCode,
    errorType,
    lineNum,
    message
  };
}

/**
 * ==============================================================================
 * BLE FAST OTA PROTOCOL (CMD 0x30 - 0x35)
 * ==============================================================================
 */

/**
 * Tạo gói mở phiên OTA (CMD 0x30):
 * Payload: FileSize (uint32_t BE, 4B) + File CRC-32 (uint32_t BE, 4B)
 */
export function buildOtaBeginFrame(fileSize, crc32) {
  const payload = new Uint8Array(8);
  payload[0] = (fileSize >>> 24) & 0xFF;
  payload[1] = (fileSize >>> 16) & 0xFF;
  payload[2] = (fileSize >>> 8) & 0xFF;
  payload[3] = fileSize & 0xFF;
  payload[4] = (crc32 >>> 24) & 0xFF;
  payload[5] = (crc32 >>> 16) & 0xFF;
  payload[6] = (crc32 >>> 8) & 0xFF;
  payload[7] = crc32 & 0xFF;
  return buildFrame(COMMANDS.OTA_BEGIN, payload);
}

/**
 * Tạo gói dữ liệu nhị phân OTA (CMD 0x32):
 * Payload: ChunkIndex (uint16_t BE, 2B) + Data bytes (M <= 480B)
 */
export function buildOtaDataFrame(chunkIndex, chunkBytes) {
  const data = chunkBytes instanceof Uint8Array ? chunkBytes : new Uint8Array(chunkBytes);
  const payload = new Uint8Array(2 + data.length);
  payload[0] = (chunkIndex >>> 8) & 0xFF;
  payload[1] = chunkIndex & 0xFF;
  payload.set(data, 2);
  return buildFrame(COMMANDS.OTA_DATA, payload);
}

/**
 * Tạo gói kết thúc stream OTA (CMD 0x33):
 * Payload: File CRC-32 (uint32_t BE, 4B)
 */
export function buildOtaEndFrame(crc32) {
  const payload = new Uint8Array(4);
  payload[0] = (crc32 >>> 24) & 0xFF;
  payload[1] = (crc32 >>> 16) & 0xFF;
  payload[2] = (crc32 >>> 8) & 0xFF;
  payload[3] = crc32 & 0xFF;
  return buildFrame(COMMANDS.OTA_END, payload);
}

/**
 * Tạo gói hủy phiên OTA (CMD 0x35):
 * Payload: ReasonCode (uint8_t, 1B)
 * 0x01: Timeout, 0x02: Hủy bởi người dùng, 0x03: Lỗi thứ tự chunk
 */
export function buildOtaAbortFrame(reasonCode = 0x02) {
  const payload = new Uint8Array([reasonCode & 0xFF]);
  return buildFrame(COMMANDS.OTA_ABORT, payload);
}

/**
 * Giải mã gói thông báo sẵn sàng nhận OTA (CMD 0x31) từ robot
 * Payload: StatusCode (1B) + Optimal ChunkSize (uint16_t BE, 2B)
 */
export function parseOtaReady(payload) {
  if (payload.length < 3) return null;
  const statusCode = payload[0];
  const chunkSize = (payload[1] << 8) | payload[2];
  return {
    statusCode,
    isReady: statusCode === 0x00,
    chunkSize: chunkSize || 480
  };
}

/**
 * Giải mã gói kết quả OTA (CMD 0x34) từ robot
 * Payload: ResultCode (1B) + RebootCountdown (1B, giây)
 */
export function parseOtaResult(payload) {
  if (payload.length < 2) return null;
  const resultCode = payload[0];
  const rebootCountdown = payload[1];
  const resultMessages = {
    0x00: 'Nâng cấp firmware thành công! Đang khởi động lại...',
    0x01: 'Lỗi toàn vẹn dữ liệu: Sai mã băm CRC-32',
    0x02: 'Lỗi ghi phân vùng bộ nhớ SPI Flash'
  };
  return {
    resultCode,
    isSuccess: resultCode === 0x00,
    rebootCountdown,
    message: resultMessages[resultCode] || 'Mã kết quả không xác định'
  };
}

/**
 * Giải mã gói thông báo hủy OTA (CMD 0x35)
 */
export function parseOtaAbort(payload) {
  if (payload.length < 1) return null;
  const reasonCode = payload[0];
  const reasonMessages = {
    0x01: 'Quá thời gian chờ phản hồi (Timeout)',
    0x02: 'Hủy bởi người dùng',
    0x03: 'Lỗi sai thứ tự gói nhị phân (Out-of-Order Chunk)'
  };
  return {
    reasonCode,
    message: reasonMessages[reasonCode] || 'Hủy không rõ lý do'
  };
}

/**
 * Giải mã gói thông báo tiến độ nạp (Flow Control ACK - CMD 0x36)
 * Payload: ChunkIndex đã ghi Flash thành công (uint16_t BE, 2B)
 */
export function parseOtaAck(payload) {
  if (!payload || payload.length < 2) return null;
  const chunkIdx = (payload[0] << 8) | payload[1];
  return { chunkIdx };
}

/**
 * Giải mã gói dữ liệu log (CMD 0x51) từ robot
 * Payload: ChunkIndex (1B) + IsLast (1B) + Text Chunk (UTF-8)
 */
export function parseLogDataPacket(payload) {
  if (!payload || payload.length < 2) return null;
  const chunkIndex = payload[0];
  const isLast = payload[1] === 1;
  const textChunk = safeDecodeUtf8(payload.slice(2));
  return {
    chunkIndex,
    isLast,
    textChunk
  };
}

/**
 * Phân loại màu sắc theo không gian màu HSV & tỷ lệ chuẩn hóa (8 Màu chuẩn STEM)
 * @param {number} r - Kênh Đỏ (0 - 255)
 * @param {number} g - Kênh Xanh lá (0 - 255)
 * @param {number} b - Kênh Xanh dương (0 - 255)
 * @returns {number} Color ID (0: UNKNOWN, 1: RED, 2: GREEN, 3: BLUE, 4: YELLOW, 5: WHITE, 6: BLACK, 7: BROWN, 8: PURPLE)
 */
export function classifyColorRgb(r, g, b) {
  const total = r + g + b;

  // BƯỚC 1: Kiểm tra Màu Đen (Hạ thấp ngưỡng cắt)
  // Chỉ kết luận là BLACK khi R < 25 VÀ G < 25 VÀ B < 25 (hoặc tổng R+G+B < 70)
  if (total === 0 || (r < 25 && g < 25 && b < 25) || total < 70) {
    return 6; // BLACK
  }

  // Chuyển đổi RGB sang [0.0, 1.0] tỷ lệ chuẩn hóa
  const r_norm = r / total;
  const g_norm = g / total;
  const b_norm = b / total;

  const max_c = Math.max(r_norm, g_norm, b_norm);
  const min_c = Math.min(r_norm, g_norm, b_norm);
  const delta = max_c - min_c;

  // Tính Hue (0 - 360 độ)
  let hue = 0;
  if (delta > 0) {
    if (max_c === r_norm) {
      hue = (60 * ((g_norm - b_norm) / delta) + 360) % 360;
    } else if (max_c === g_norm) {
      hue = (60 * ((b_norm - r_norm) / delta) + 120) % 360;
    } else {
      hue = (60 * ((r_norm - g_norm) / delta) + 240) % 360;
    }
  }

  const sat = max_c === 0 ? 0 : (delta / max_c);
  const max_rgb = Math.max(r, g, b);
  const val = max_rgb / 255.0;

  // BƯỚC 2: Kiểm tra Màu Trắng (sat < 0.20 VÀ val > 0.40 VÀ total > 350)
  if (sat < 0.20 && val > 0.40 && total > 350) {
    return 5; // WHITE
  }

  // BƯỚC 3: Phân loại theo góc Hue (Khi sat >= 0.20)
  if (sat >= 0.20) {
    // Màu Đỏ (Red): hue >= 345° HOẶC hue < 20°
    if (hue >= 345 || hue < 20) {
      return 1; // RED
    }
    // Màu Nâu (Brown) vs Màu Vàng (Yellow): nằm trong dải hue in [20°, 75°]
    if (hue >= 20 && hue <= 75) {
      if (val < 0.55 || total < 380) {
        return 7; // BROWN
      } else {
        return 4; // YELLOW
      }
    }
    // Màu Xanh Lá (Green): hue in [80°, 165°]
    if (hue >= 80 && hue <= 165) {
      return 2; // GREEN
    }
    // Màu Xanh Dương (Blue): hue in [180°, 255°]
    if (hue >= 180 && hue <= 255) {
      return 3; // BLUE
    }
    // Màu Tím (Purple): hue in [260°, 330°] (đồng thời B > G VÀ R > G)
    if (hue >= 260 && hue <= 330 && b > g && r > g) {
      return 8; // PURPLE
    }
  }

  return 0; // UNKNOWN
}

